
import { navigateToWrite } from '@/utils/navigationUtils';
import {
  Vue,
  Component,
} from 'vue-property-decorator';
import Pagination from '@/components/board/pagination.vue';
import { getCompanyInfo, searchCompany, addCompanyLog } from '@/api/company';
import { getUserInfo } from '@/api/user';
import { ICompanySearch, ICompanyLog } from '@/types/company';
import { UserModule } from '@/store/user';

@Component({
  name: 'InvestCompanyList',
  components: {
    Pagination,
  },
})

export default class extends Vue {
  private readonly SESSION_KEY = 'INVEST_LIST_STATE';

  private uData: any = {};

  private frCname = '';

  private toCname = '';

  private sort = '';

  async created() {
    if (UserModule.token) {
      const uRes = await getUserInfo();
      const { uid } = uRes.data;
      if (uid) {
        const { data } = await getCompanyInfo(uid);
        this.uData = data;
      }
    }
  }

  // 로그인 하지 않은 사용자 접근 제한
  checkAuthAndNavigate(uid: string): void {
    if (!UserModule.token) {
      alert('로그인이 필요한 서비스입니다.');
    } else {
      const state = {
        listQuery: this.listQuery,
        selectedGubun: this.selectedGubun,
      };
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(state));

      this.$router.push({
        name: 'InvestCompanyDetail',
        params: { investCompanyUid: uid },
      });
    }
  }

  get isIrMatchActive(): boolean {
    return this.uData.coDept === 'S';
  }

  // IR 매칭 버튼 클릭 시 로그인 체크
  private checkAuthAndNavigateToWrite(uid: string): void {
    if (!UserModule.token) {
      alert('로그인이 필요한 서비스입니다.');
    } else if (!this.isIrMatchActive) {
      alert('스타트업 회원만 투자 문의가 가능합니다.');
    } else {
      this.handleNavigateToWrite(uid);
    }
  }

  private fetchCompanies() {
    let apiPage = this.listQuery.page;
    if (apiPage < 0) apiPage = 0;

    const searchParams: ICompanySearch = {
      page: apiPage,
      size: this.listQuery.size,
      coDept: this.selectedGubun, // 투자사구분 (I,O 또는 I 또는 O)
      searchValue: this.listQuery.searchValue, // 검색어
    };

    searchCompany(searchParams)
      .then((res) => {
        this.companyList = res.data.content;
        this.totalElements = res.data.totalElements;
        this.totalPages = res.data.totalPages;
        // console.log('companyList', this.companyList);
      })
      .catch((error) => {
        console.error('IR 기업 검색 실패(500 에러 등):', error);
      });
  }

  mounted() {
    this.init();
  }

  private listQuery: any = {
    page: 0,
    size: 9,
    searchValue: '',
    sort: 'progress',
    progress: 0,
    categories: [],
    target: '',
    categories2: '',
    startDate: '',
    endDate: '',
  };

  private companyList: any = [];

  private selectedGubun = 'I';

  // 투자사 클릭
  private handleClickGubun(uid: string) {
    this.selectedGubun = uid;
  }

  private regionalCategories: any = [
    { categoryName: '투자사', uid: 'I' },
    { categoryName: '대중견기업', uid: 'O' },
  ];

  private totalElements = 0;

  private totalPages = 1;

  get imagePath() {
    return `${process.env.VUE_APP_COMMON_API}/attached-file`;
  }

  private async init() {
    const savedState = sessionStorage.getItem(this.SESSION_KEY);

    // 데이터를 읽은 후 즉시 세션에서 삭제 (다른 메뉴 이동 시 초기화를 위함)
    sessionStorage.removeItem(this.SESSION_KEY);

    if (savedState) {
      // 상세페이지에서 돌아온 경우 복구
      const parsed = JSON.parse(savedState);
      this.listQuery = parsed.listQuery;
      this.selectedGubun = parsed.selectedGubun;

      this.fetchCompanies();
    } else {
      // 다른 메뉴에서 진입 시 기본값
      this.listQuery.page = 0;
      this.listQuery.searchValue = '';
      this.selectedGubun = 'I';
      this.fetchCompanies();
    }
  }

  // 검색 버튼 클릭 시
  private handleSearch() {
    this.listQuery.page = 0;
    this.fetchCompanies();
  }

  // 페이지 변경 시
  private handleChangePage(page: number) {
    this.listQuery.page = page;
    this.fetchCompanies();
  }

  // 초기화 버튼
  private searchReset() {
    sessionStorage.removeItem(this.SESSION_KEY);
    this.listQuery.searchValue = '';
    this.selectedGubun = 'I';
    this.listQuery.page = 0;
    this.fetchCompanies();
  }

  private getThumnail(inputValue: string) {
    if (inputValue.startsWith('http')) return inputValue;
    return `${this.imagePath}/${inputValue}`;
  }

  private getNumber(index: number) {
    const totalSize = (this.listQuery.page - 1) * this.listQuery.size;
    return this.totalElements - totalSize - index;
  }

  private handleNavigateToWrite(companyId: string) {
    const company = this.companyList.find((c: any) => c.uid === companyId || c.userUid === companyId);

    if (company) {
      this.sendLog('IR 매칭', company);
      navigateToWrite(this.$router, this.$route, company.userUid);
    }
  }

  // 로그 sort
  private calculateSort(coData: any) {
    if (coData?.coDept && this.uData?.coDept) {
      this.sort = `${this.uData.coDept}${coData.coDept}`;
      // console.log('최종 sort:', this.sort);
    }
  }

  // 로그 추가 함수
  private async logCompanyAction(data: ICompanyLog) {
    try {
      await addCompanyLog(data);
      // console.log('로그 추가 성공:', data);
    } catch (error) {
      // console.error('로그 추가 실패:', error);
    }
  }

  // 로그 데이터 생성
  private sendLog(part: string, coData: any) {
    this.calculateSort(coData);
    const logData: ICompanyLog = {
      part,
      frCname: this.frCname,
      toCname: coData.coName,
      sort: this.sort,
    };
    this.logCompanyAction(logData);
  }
}
